import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { FramingSceneInspector } from "../framingInspector";

/**
 * Bounded attempt selector (CROP-WB-011). Rendered entirely from sanitized
 * attempt summaries: selecting an attempt triggers exactly one lazy
 * selected-attempt fetch and never signs media for the others.
 */
export function FramingAttemptSelector({
  inspector,
  selectedAttemptId,
  onSelect,
}: {
  inspector: FramingSceneInspector;
  selectedAttemptId: string | null;
  onSelect: (attemptId: string | null) => void;
}) {
  const attempts = inspector.attempts;
  if (attempts.length === 0) {
    return (
      <Badge variant="outline">
        attempt {inspector.attemptId ?? "legacy"} (no attempt catalog)
      </Badge>
    );
  }
  const effective =
    selectedAttemptId ??
    attempts.find((attempt) => attempt.isDefault)?.attemptId ??
    inspector.attemptId ??
    attempts[0].attemptId;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        value={effective}
        onValueChange={(value) => {
          const isDefault = attempts.find(
            (attempt) => attempt.attemptId === value,
          )?.isDefault;
          onSelect(isDefault ? null : value);
        }}
      >
        <SelectTrigger size="sm" className="min-w-56" aria-label="Attempt">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {attempts.map((attempt) => (
            <SelectItem key={attempt.attemptId} value={attempt.attemptId}>
              {attempt.attemptId}
              {attempt.isDefault ? " · accepted" : ` · ${attempt.status}`}
              {attempt.completedAt ? ` · ${attempt.completedAt}` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {inspector.attemptsTruncated ? (
        <Badge variant="outline">
          showing {attempts.length} of {inspector.attemptsTotal ?? "?"} attempts
        </Badge>
      ) : null}
      {inspector.attemptAvailability === "correlation_mismatch" ? (
        <Badge variant="destructive">
          requested attempt does not correlate
        </Badge>
      ) : null}
    </div>
  );
}
